/**
 * Kailar newsletter signup — posts straight to Klaviyo instead of using
 * Shopify's `{% form 'customer' %}`.
 *
 * Why not the Shopify form: it creates a customer with marketing consent on the
 * spot and no confirmation mail. Klaviyo then picks that profile up through the
 * Shopify integration, which bypasses the list's double opt-in entirely.
 *
 * Why not klaviyo.js: it sets cookies before consent (§ 25 TDDDG). One
 * unauthenticated POST is all we need here.
 *
 * Why this endpoint and not the newer /client/subscriptions/ JSON API: that one
 * answers browser requests without any Access-Control-Allow-Origin header, so
 * every call dies in the CORS layer before it reaches Klaviyo — checked from the
 * shop origin and locally, with and without preflight (2026-07-26). The endpoint
 * used here is the one Klaviyo's own embedded forms post to; it answers with
 * proper CORS headers and honours the list's opt-in setting.
 *
 * The opt-in behaviour lives on the list, not on this call. The target list is
 * double opt-in, so a success response means "confirmation mail on its way" —
 * never "subscribed". The success copy has to say so.
 */
(function () {
  'use strict';

  // Footer and newsletter section both pull this file in. Two <script src> tags
  // with the same URL execute twice, which would register the submit listener
  // twice and fire two subscription requests per signup.
  if (window.kailarSubscribeReady) return;
  window.kailarSubscribeReady = true;

  var ENDPOINT = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';

  /**
   * Klaviyo answers `success: true` for addresses it then silently discards, so
   * a typo would look exactly like a signup that worked. The browser's own
   * `type="email"` check is no help either — it happily accepts `lea@gmail`.
   * Hence this: a domain needs at least two labels and an ending that looks like
   * one. Deliberately permissive beyond that; rejecting a valid address is worse
   * than letting an odd one through.
   */
  function looksLikeEmail(value) {
    var at = /^[^\s@]+@([^\s@]+)$/.exec(value);
    if (!at) return false;

    var labels = at[1].split('.');
    if (labels.length < 2) return false;

    for (var i = 0; i < labels.length; i++) {
      if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i.test(labels[i])) return false;
    }

    var tld = labels[labels.length - 1];
    return tld.length >= 2 && /[a-z]/i.test(tld);
  }

  function setStatus(form, kind, message) {
    var status = form.querySelector('[data-subscribe-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('kailar-subscribe__status--error', kind === 'error');
    status.classList.toggle('kailar-subscribe__status--success', kind === 'success');
  }

  function submit(form) {
    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var trap = form.querySelector('[data-subscribe-trap]');
    var consent = form.querySelector('[data-subscribe-consent]');
    var listId = form.getAttribute('data-list-id');

    if (!input || !listId) return;

    // Bots fill every field they can find; this one is off-screen for humans.
    // Answer them with the success message so they learn nothing.
    if (trap && trap.value) {
      setStatus(form, 'success', form.getAttribute('data-text-success'));
      return;
    }

    if (!looksLikeEmail(input.value.trim())) {
      setStatus(form, 'error', form.getAttribute('data-text-invalid'));
      input.focus();
      return;
    }

    // Only the popup carries an explicit consent box. The form is `novalidate`,
    // so nothing else would enforce it.
    if (consent && !consent.checked) {
      setStatus(form, 'error', form.getAttribute('data-text-consent'));
      consent.focus();
      return;
    }

    form.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    setStatus(form, 'pending', form.getAttribute('data-text-pending'));

    var body = new URLSearchParams();
    body.set('g', listId);
    body.set('email', input.value.trim());
    body.set('$fields', '$source');
    body.set('$source', form.getAttribute('data-source') || 'Website');

    // 'omit' keeps the browser from storing Klaviyo's Cloudflare cookie — the
    // whole point of avoiding klaviyo.js was not setting cookies without consent.
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        // `is_subscribed` is not a success signal: it comes back false on a
        // double opt-in list (correct — the confirmation is still pending) and
        // true for input Klaviyo silently discarded.
        if (!payload || payload.success !== true) {
          throw new Error((payload && payload.errors && payload.errors.join(', ')) || 'unknown error');
        }

        // Klaviyo echoes the address back only when it actually created a
        // profile and sent the confirmation mail. Without it, it already knew
        // the address and sent nothing — so promising a fresh mail would be a
        // lie to exactly the person who signed up twice because they couldn't
        // find the first one.
        var fresh = !!(payload.data && payload.data.email);
        var known = form.getAttribute('data-text-known');

        var field = form.querySelector('[data-subscribe-field]');
        if (field) field.hidden = true;
        form.classList.add('kailar-subscribe--done');
        setStatus(form, 'success', !fresh && known ? known : form.getAttribute('data-text-success'));
      })
      .catch(function () {
        setStatus(form, 'error', form.getAttribute('data-text-error'));
        if (button) button.disabled = false;
      })
      .then(function () {
        form.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-kailar-subscribe]');
    if (!form) return;
    event.preventDefault();
    submit(form);
  });
})();
